import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart3, Target, Shield } from 'lucide-react';
import LightBackground from '@/components/LightBackground';

const CookiePolicy = () => {
  const lastUpdated = 'May 13, 2026';

  const categories = [
    {
      icon: Shield,
      title: 'Strictly Necessary Cookies',
      desc: 'Required for the website to function (authentication, session, security, load balancing). These cannot be disabled.',
      examples: 'sb-access-token, sb-refresh-token, cookie_consent',
    },
    {
      icon: Settings,
      title: 'Functional Cookies',
      desc: 'Remember your preferences such as language, theme, and login state to personalize your experience.',
      examples: 'theme, language, lovable_session',
    },
    {
      icon: BarChart3,
      title: 'Analytics Cookies',
      desc: 'Help us understand how visitors interact with the site so we can improve it. Data is aggregated and anonymized.',
      examples: 'Google Analytics (_ga, _gid), internal page-view tracking',
    },
    {
      icon: Target,
      title: 'Marketing & Advertising Cookies',
      desc: 'Used by Meta (Facebook), Google Ads, and Taboola to measure ad performance and show relevant content. Set only after consent.',
      examples: '_fbp, fr, _gcl_au, taboola_session',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Cookie Policy – Faceless Aura Club</title>
        <meta
          name="description"
          content="Cookie Policy for Faceless Aura Club. Learn which cookies we use, why we use them, and how to manage your preferences."
        />
        <link rel="canonical" href="https://facelessaura.club/cookies" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <Cookie className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">Cookie Policy</h1>
            <p className="text-slate-500">Last updated: {lastUpdated}</p>
          </div>

          <div className="bg-white/70 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 md:p-10 space-y-8 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files placed on your device when you visit our website. They allow the site to
                remember your actions and preferences (such as login, language, and display settings) over a period of
                time, so you don't have to re-enter them whenever you come back.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">2. How We Use Cookies</h2>
              <p>
                Faceless Aura Club (operated by AURADYNAMICS PROJECT MANAGEMENT SERVICES - FZCO) uses cookies and
                similar technologies (local storage, pixels) to operate the website, secure your account, measure
                performance, and personalize marketing.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Categories of Cookies We Use</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {categories.map((c) => (
                  <div key={c.title} className="border border-slate-200 rounded-xl p-5 bg-white/50">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <c.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{c.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{c.desc}</p>
                    <p className="text-xs text-slate-500"><span className="font-medium">Examples:</span> {c.examples}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">4. Third-Party Cookies</h2>
              <p>
                Some cookies are placed by third-party services that appear on our pages, including Meta (Facebook
                Pixel and Conversions API), Google (Analytics & Ads), Taboola, Stripe (payment processing), and
                Supabase / Lovable Cloud (authentication & infrastructure). These providers process limited data
                under their own privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">5. Your Choices & Consent</h2>
              <p className="mb-3">
                When you first visit our website, a cookie banner asks for your consent before any non-essential
                cookies are set. You can change or withdraw your consent at any time by clearing your browser data
                or using the consent prompt that re-appears.
              </p>
              <p>
                Most browsers allow you to refuse or delete cookies via their settings. Disabling strictly necessary
                cookies may impact site functionality (e.g., you may not be able to log in).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">6. Retention</h2>
              <p>
                Session cookies expire when you close your browser. Persistent cookies remain for a defined period
                (typically 30 days to 24 months) depending on their purpose. Marketing cookies are kept no longer
                than 13 months.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">7. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy to reflect changes in technology, regulation, or our practices. The
                "Last updated" date at the top will always reflect the latest revision.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">8. Contact</h2>
              <p>
                Questions about cookies or your data?{' '}
                <a href="mailto:hello@auradynamics.management" className="text-blue-600 hover:underline">
                  hello@auradynamics.management
                </a>
                . See also our{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> and{' '}
                <Link to="/legal" className="text-blue-600 hover:underline">Legal Notice</Link>.
              </p>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default CookiePolicy;
